/**
 * 多言語の通し試聴 (「▶ 多言語を通しで試聴」) の状態と操作。
 *
 * ボタンは操作ボタンの行に、再生中の文面とエラーはその行の外に置きたい
 * (行の中に出すと、文面の幅でボタンが次の段へ押し出される)。置き場所が分かれるため、
 * 状態は 1 か所 (このフック) に持ち、ボタンと表示の 2 部品で共有する。
 */
import { useEffect, useRef, useState } from 'react';

import type { BroadcastContent } from '../../../api/types';
import {
  useKaraokePlayback,
  type UseKaraokePlaybackResult,
} from '../../../hooks/useKaraokePlayback';
import { describeApiErrorMessage } from '../../../utils/apiErrorMessage';
import { ssmlToPlainText } from '../../../utils/karaoke';
import { playSequential, type SequentialAudioItem } from '../../../utils/sequentialAudio';
import { usePreviewVoiceSelections } from '../PreviewVoiceContext';
import { buildPreviewQueue } from '../previewLanguages';
import type { PreviewSampleContext, PreviewSsmlHandler } from '../types';

/** ボタンと表示で共有する状態と操作。 */
export interface MultilingualPreviewState {
  playing: boolean;
  error: string | null;
  karaoke: UseKaraokePlaybackResult;
  /** 再生中なら停止し、そうでなければ生成して再生を始める。 */
  toggle: () => void;
}

export function useMultilingualPreview({
  content,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  resolveSample,
  onPlayingChange,
}: {
  content: BroadcastContent;
  parentVoiceId?: string;
  playbackRate?: number;
  onPreviewSsml: PreviewSsmlHandler;
  resolveSample: () => PreviewSampleContext;
  onPlayingChange: (playing: boolean) => void;
}): MultilingualPreviewState {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const voiceSelections = usePreviewVoiceSelections();
  // Issue #643: 再生中の言語の文面をカラオケ風に表示する。
  const karaoke = useKaraokePlayback();
  useEffect(() => () => controllerRef.current?.abort(), []);

  const start = async (): Promise<void> => {
    if (controllerRef.current) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    setPlaying(true);
    onPlayingChange(true);
    setError(null);
    const items: SequentialAudioItem[] = [];
    const skipped: string[] = [];
    const sampleContext = resolveSample();
    try {
      for (const { label, ...input } of buildPreviewQueue(
        [content],
        parentVoiceId,
        voiceSelections,
      )) {
        controller.signal.throwIfAborted();
        try {
          const result = await onPreviewSsml({ ...input, sampleContext });
          items.push(
            karaoke.attach({
              url: result.url,
              label,
              playbackRate,
              text: ssmlToPlainText(result.renderedSsml),
            }),
          );
        } catch {
          skipped.push(label);
        }
      }
      controller.signal.throwIfAborted();
      if (items.length > 0) {
        await playSequential(items, undefined, controller.signal);
      }
      if (skipped.length > 0) {
        setError(`一部を試聴できませんでした: ${skipped.join(', ')}`);
      }
    } catch (failure) {
      if (!controller.signal.aborted)
        setError(describeApiErrorMessage(failure, '試聴に失敗しました'));
    } finally {
      items.forEach((item) => URL.revokeObjectURL(item.url));
      controllerRef.current = null;
      karaoke.clear();
      setPlaying(false);
      onPlayingChange(false);
    }
  };

  const toggle = (): void => {
    if (playing) controllerRef.current?.abort();
    else void start();
  };

  return { playing, error, karaoke, toggle };
}
